import React, { useContext } from 'react';

import Button from 'kingpin/atoms/Button';
import ContextMenu, { Option } from 'kingpin/atoms/ContextMenu';
import RolesContext from 'contexts/RolesContext';
import BonusPeriodsContext from 'contexts/BonusPeriodsContext';
import PERMISSIONS from 'permissionDefinitions';
import { BonusOverviewContext } from 'components/DriverAX/providers/BonusOverviewProvider';
import useRecognitionPaymentAccess from 'components/DriverAX/components/AdditionalPayments/useRecognitionPaymentAccess';

import getAdjustOptions, { AdjustOptionKey } from './getAdjustOptions';

// Wide enough for "Manage Additional Payments" on one line (the default
// option width is 192px).
const ADJUST_OPTION_WIDTH = 240;

// The slideout's entry point for changing a driver's bonus: Override and the
// recognition payment flows (a plain Override button when that's all there
// is). See getAdjustOptions for the rules.
const AdjustMenu = ({
  isOverrideEnabled,
  onOverrideClicked,
}: {
  // The board offers the Override button (isSlideoutOverrideButtonEnabled).
  isOverrideEnabled: boolean;
  onOverrideClicked: () => void;
}) => {
  const { currentPermissions } = useContext(RolesContext);
  const { selectedBonusPeriod, selectedSubPeriod } =
    useContext(BonusPeriodsContext);
  const { enterAdditionalPaymentsMode } = useContext(BonusOverviewContext);
  const { canAdd, hasPayments } = useRecognitionPaymentAccess();

  const menu = getAdjustOptions({
    selectedBonusPeriod,
    selectedSubPeriod,
    canOverride:
      isOverrideEnabled &&
      currentPermissions.includes(PERMISSIONS.DRIVER_BONUS.OVERRIDE_CATEGORIES),
    canAddRecognitionPayments: canAdd,
    hasRecognitionPayments: hasPayments,
  });

  if (!menu) {
    return null;
  }

  const onOptionClicked = (key: AdjustOptionKey) => {
    if (key === 'override') {
      onOverrideClicked();
    } else if (key === 'add-recognition-payment') {
      enterAdditionalPaymentsMode('add');
    } else {
      enterAdditionalPaymentsMode('manage');
    }
  };

  // Programs without recognition payments keep the plain Override button, so
  // the menu only appears when there's more than one kind of adjustment.
  if (menu.options.length === 1 && menu.options[0].key === 'override') {
    const [override] = menu.options;
    return (
      <Button
        size={'Medium'}
        type={'Primary'}
        label={override.label}
        onClick={onOverrideClicked}
        isDisabled={override.isDisabled}
        isDisabledMessage={override.disabledMessage}
        testId={'override-button'}
      />
    );
  }

  return (
    <ContextMenu
      label={'Adjust'}
      icon={'edit-filled'}
      iconAfter={'chevron-down'}
      buttonType={'Primary'}
      size={'Medium'}
      placement={'bottom-end'}
      isDisabled={menu.isDisabled}
      disabledMessage={menu.disabledMessage}
      testId={'adjust-menu'}
    >
      {menu.options.map((option) => (
        <Option
          key={option.key}
          label={option.label}
          onClick={() => onOptionClicked(option.key)}
          isDisabled={option.isDisabled}
          disabledMessage={option.disabledMessage}
          testId={`adjust-${option.key}`}
          width={ADJUST_OPTION_WIDTH}
        />
      ))}
    </ContextMenu>
  );
};

export default AdjustMenu;
